import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { endpoints } from 'src/environments/endpoints';
import { IAppState } from '../../store/interfaces/IAppState';
import { selectTenantIdWithoutBranch, selectToken } from '../../store/selectors';
import {
  SendSolicitationMessageRequest,
  SolicitationMessage,
  UnreadCount,
} from '../../models/SolicitationMessage';

/**
 * Serviço do chat de solicitações. Cobre tanto a parte REST (histórico, envio,
 * leitura e contagem de não lidas) quanto a conexão em tempo real (SignalR).
 *
 * O envio é "persist-then-broadcast": o POST grava no banco e o backend propaga
 * a mensagem pelo Hub. Por isso o histórico funciona mesmo offline — ao reconectar,
 * basta recarregar via REST.
 */
@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private connection?: HubConnection;

  private messageReceived$ = new Subject<SolicitationMessage>();
  private messageRead$ = new Subject<{ callcode: string; readerRole: string }>();
  private unreadChanged$ = new Subject<{ callcode: string }>();
  private reconnected$ = new Subject<void>();

  constructor(private http: HttpClient, private store: Store<IAppState>) {}

  // ---------------------------------------------------------------------------
  // REST
  // ---------------------------------------------------------------------------

  getMessages(callcode: string): Observable<SolicitationMessage[]> {
    const params = { callcode };
    return this.http.get<SolicitationMessage[]>(endpoints.solicitationMessages, { params });
  }

  sendMessage(request: SendSolicitationMessageRequest): Observable<SolicitationMessage> {
    return this.http.post<SolicitationMessage>(endpoints.solicitationMessages, request);
  }

  markAsRead(callcode: string): Observable<void> {
    const url = `${endpoints.solicitationMessages}/mark-read`;
    return this.http.post<void>(url, { callcode });
  }

  /** Contagem de não lidas pelo cliente para o badge da lista de chamados. */
  getMyUnreadCounts(callcodes: string[]): Observable<UnreadCount[]> {
    const url = `${endpoints.solicitationMessages}/minhas-nao-lidas`;
    return this.http.post<UnreadCount[]>(url, { callcodes });
  }

  // ---------------------------------------------------------------------------
  // Tempo real (SignalR)
  // ---------------------------------------------------------------------------

  get onMessageReceived(): Observable<SolicitationMessage> {
    return this.messageReceived$.asObservable();
  }

  get onMessageRead(): Observable<{ callcode: string; readerRole: string }> {
    return this.messageRead$.asObservable();
  }

  get onUnreadChanged(): Observable<{ callcode: string }> {
    return this.unreadChanged$.asObservable();
  }

  /** Emite quando a conexão é restabelecida (grupos precisam ser refeitos). */
  get onReconnected(): Observable<void> {
    return this.reconnected$.asObservable();
  }

  /**
   * Abre a conexão com o Hub e entra no grupo da solicitação (quando informada).
   * O token vai via accessTokenFactory; tenantId/callcode vão na query string
   * (o backend usa tenantId para agrupar a entrega por tenant).
   */
  async connect(callcode?: string): Promise<void> {
    // Aguarda token e tenant disponíveis (no bypass do analista o enterprise
    // pode ainda estar carregando quando a aba abre).
    const token = await firstValueFrom(
      this.store.select(selectToken).pipe(filter((t): t is string => !!t), take(1))
    );
    const tenantId = await firstValueFrom(
      this.store.select(selectTenantIdWithoutBranch).pipe(filter((t): t is string => !!t), take(1))
    );

    if (this.connection) {
      await this.disconnect();
    }

    const query = new URLSearchParams();
    query.set('tenantId', tenantId);
    if (callcode) {
      query.set('callcode', callcode);
    }

    const hubUrl = `${environment.hubUrl}?${query.toString()}`;

    this.connection = new HubConnectionBuilder()
      .withUrl(hubUrl, { accessTokenFactory: () => token })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    this.registerHandlers();

    await this.connection.start();
  }

  /** Entra no grupo de uma solicitação sem reconectar. */
  async joinCallcode(callcode: string): Promise<void> {
    const tenantId = (await firstValueFrom(this.store.select(selectTenantIdWithoutBranch).pipe(take(1)))) ?? '';
    if (this.connection?.state === HubConnectionState.Connected) {
      await this.connection.invoke('JoinCallcode', tenantId, callcode);
    }
  }

  /** Entra nos grupos de vários callcodes (lista de chamados). */
  async joinCallcodes(callcodes: string[]): Promise<void> {
    for (const callcode of callcodes) {
      await this.joinCallcode(callcode);
    }
  }

  async disconnect(): Promise<void> {
    if (this.connection) {
      try {
        await this.connection.stop();
      } finally {
        this.connection = undefined;
      }
    }
  }

  private registerHandlers(): void {
    if (!this.connection) {
      return;
    }

    this.connection.on('ReceberMensagem', (message: SolicitationMessage) => {
      this.messageReceived$.next(message);
    });

    this.connection.on('MensagemLida', (payload: { callcode: string; readerRole: string }) => {
      this.messageRead$.next(payload);
    });

    this.connection.on('MensagensNaoLidasAlteradas', (payload: { callcode: string }) => {
      this.unreadChanged$.next(payload);
    });

    // Após reconectar, os grupos são perdidos pelo SignalR e precisam ser refeitos.
    this.connection.onreconnected(() => this.reconnected$.next());
  }
}
